import _ from 'lodash'
import type {
    CompRef,
    Pointer,
    RawComponentExportStructure,
    DependenciesManager,
    RawImportExportOptions,
    Namespace,
    DalValue,
    ComponentFeature,
    DataItemsMap,
    ResolvedDataItem,
    DependenciesBuilder
} from '@wix/document-services-types'
import type {CreateExtArgs, DAL} from '@wix/document-manager-core'
import {getResolvedDataItemsByNamespaceMap, updateDataItemsByNamespacesDataMap} from './dataItems'
import type {DataModelExtensionAPI} from '../../../../dataModel/dataModel'
import type {ComponentDefinitionExtensionAPI} from '../../../../componentDefinition'
import {getComponentFeaturesByType, getFeaturesInfoByComponentFeatures} from '../../utils'
import {
    getCompIdWithRepeatersNesting,
    isReferredId,
    replaceRefComponentIdInOverride
} from '../../../../../utils/inflationUtils'
import {COMPONENT_FEATURES} from '../../constants'
import {stripHashIfExists} from '../../../../../utils/refArrayUtils'
import {getRepeaterItemId} from '../../../../../utils/repeaterUtils'

const getOverrides = (
    {pointers, extensionAPI}: CreateExtArgs,
    component: RawComponentExportStructure,
    componentPointer: CompRef,
    namespaces: Set<Namespace>
): Pointer[] => {
    const {dataModel, componentDefinition} = extensionAPI as DataModelExtensionAPI & ComponentDefinitionExtensionAPI
    const overridesPointers = dataModel.components.getAllRepeaterOverridesForComponent(componentPointer)

    if (componentDefinition.isRefComponent(component.structure.componentType)) {
        overridesPointers.push(...pointers.full.referredStructure.getAllOverrides(componentPointer))
    }

    return overridesPointers.filter(pointer => namespaces.has(pointer.type))
}

const mapOverridesIds = (
    overrides: RawComponentExportStructure['overrides'],
    iteratee: (id: string, namespace: Namespace) => string
): RawComponentExportStructure['overrides'] => {
    if (overrides === undefined) {
        return
    }

    return _.mapValues(overrides, (featureOverrides: DataItemsMap, namespace: Namespace) =>
        _.mapKeys(featureOverrides, (override: ResolvedDataItem, overrideId: string) => iteratee(overrideId, namespace))
    )
}

const stripRepeaterOverridesIds = (
    overrides: RawComponentExportStructure['overrides']
): RawComponentExportStructure['overrides'] =>
    mapOverridesIds(overrides, (overrideId: string) => {
        if (isReferredId(overrideId)) {
            return overrideId
        }

        return getRepeaterItemId(overrideId)
    })

export const resolveOverrides = (
    extensionContext: CreateExtArgs,
    component: RawComponentExportStructure,
    componentPointer: CompRef,
    pagePointer: CompRef,
    {componentFeatures}: RawImportExportOptions,
    dependenciesBuilder?: DependenciesBuilder
) => {
    const relevantFeatures = getComponentFeaturesByType(component.structure.componentType, componentFeatures)
    const {namespaces} = getFeaturesInfoByComponentFeatures(relevantFeatures)
    const overridesPointers = getOverrides(extensionContext, component, componentPointer, namespaces)

    const overrides = getResolvedDataItemsByNamespaceMap(
        extensionContext,
        overridesPointers,
        pagePointer,
        dependenciesBuilder
    )
    const fixedOverrides = stripRepeaterOverridesIds(overrides)

    if (overrides !== undefined) {
        component.overrides = fixedOverrides
    }
}

const buildRepeaterOverrideId = (componentPointer: CompRef, itemId: string, namespace: Namespace, dal: DAL): string => {
    const {query} = COMPONENT_FEATURES[namespace]
    const dataId = stripHashIfExists(dal.get(componentPointer)[query])
    return getCompIdWithRepeatersNesting(dataId, [itemId])
}

const getRelevantOverrides = (
    {overrides}: RawComponentExportStructure,
    aliases: Set<ComponentFeature['alias']>
): RawComponentExportStructure['overrides'] =>
    _.pickBy(overrides, (overridesItems: Record<string, DalValue>, alias: string) => aliases.has(alias))

const fixOverridesIds = (
    overrides: RawComponentExportStructure['overrides'],
    componentPointer: CompRef,
    dal: DAL
): RawComponentExportStructure['overrides'] =>
    mapOverridesIds(overrides, (overrideId: string, namespace: Namespace) => {
        if (isReferredId(overrideId)) {
            return replaceRefComponentIdInOverride(overrideId, componentPointer.id)
        }

        return buildRepeaterOverrideId(componentPointer, overrideId, namespace, dal)
    })

export const updateOverrides = (
    extensionContext: CreateExtArgs,
    componentPointer: CompRef,
    component: RawComponentExportStructure,
    dependenciesManager: DependenciesManager,
    {componentFeatures}: RawImportExportOptions
) => {
    const {dal, pointers} = extensionContext
    const relevantFeatures = getComponentFeaturesByType(component.structure.componentType, componentFeatures)
    const {aliases, namespaces} = getFeaturesInfoByComponentFeatures(relevantFeatures)
    const origOverridesPointers = getOverrides(extensionContext, component, componentPointer, namespaces)
    const pagePointer = pointers.structure.getPageOfComponent(componentPointer) as CompRef

    const relevantOverrides = getRelevantOverrides(component, aliases)
    const fixedOverrides = fixOverridesIds(relevantOverrides, componentPointer, dal)

    updateDataItemsByNamespacesDataMap(
        extensionContext,
        origOverridesPointers,
        fixedOverrides,
        pagePointer,
        dependenciesManager
    )
}
